import parse from 'html-react-parser';
import React, { useCallback, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { JoinInCapture } from '@/components/JoinInCapture/JoinInCapture';
import arrowBack from '../../assets/svg/arrow-back.svg';
import calendarSvg from '../../assets/svg/calendar.svg';
import closeSvg from '../../assets/svg/close.svg';
import {
  Box,
  Category,
  CategoryWrapper,
  CloseButton,
  ImageNotAvailable,
  ModalContent,
  ModalDateTime,
  ModalDescription,
  ModalImage,
  ModalOverlay,
  ModalTitle,
  ModalWrapper,
  NewsCaptureBlock,
  NewsCard,
  NewsContent,
  NewsDateTime,
  NewsDescription,
  NewsGrid,
  NewsImage,
  NewsSectionContainer,
  NewsStateMessage,
  NewsTitle,
  ReadMore,
  SliderWrapper,
  SnapshotImageFallback,
  ViewAllNewsButton,
  ViewPost,
} from './News.styles';
import { useCategories, usePosts } from './queries';
import { SNAPSHOT_STORIES } from './snapshot';

interface Post {
  id: number;
  title: {
    rendered: string;
  };
  date: string;
  excerpt: {
    rendered: string;
  };
  _embedded: {
    'wp:featuredmedia': Array<{
      source_url: string;
    }>;
  };
  categories: number[];
  guid: { rendered: string };
}

const MAX_LENGTH = 100;
const TITLE_LENGTH = 60;

interface NewsProps {
  blogFilter: string;
  /** Project name for the newsletter capture log (the page has no route params). */
  project?: string;
  /** Engagement signal for the slide-in capture: fired when a story is opened. */
  onStoryClick?: () => void;
}

export const News: React.FC<NewsProps> = ({ blogFilter, project, onStoryClick }) => {
  const { data: posts = [], isLoading: postsLoading, isError: postsFailed } = usePosts();
  const {
    data: categories = {},
    isLoading: categoriesLoading,
    isError: categoriesFailed,
  } = useCategories();
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    if (selectedPost) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [selectedPost]);

  const filteredPosts = posts.filter((post) =>
    post.categories.some((categoryId) => {
      const category = categories[categoryId];
      return category && category.toLowerCase() === blogFilter.toLowerCase();
    }),
  );

  const truncateText = (html: string, maxLength: number) => {
    const plainText = html.replace(/<\/?[^>]+(>|$)/g, '');
    if (plainText.length > maxLength) {
      return `${plainText.substring(0, maxLength)}...`;
    }
    return plainText;
  };

  const handleCloseModal = useCallback(() => setSelectedPost(null), []);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    arrows: false,
    slidesToShow: 3,
    slidesToScroll: 3,
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: 1324,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  // Loading, error, and empty are three distinct visible states (CLAUDE.md
  // §13): a failed feed must never look like a slow one or an empty one, and
  // none of them may render as a silent blank. The blog button stays visible
  // in the error and empty states so the stories remain reachable.
  if (postsLoading || categoriesLoading) {
    return (
      <NewsSectionContainer>
        <NewsStateMessage>Loading the latest news…</NewsStateMessage>
      </NewsSectionContainer>
    );
  }

  if (postsFailed || categoriesFailed) {
    // Live fetch failed - expected today, since the upstream is dead
    // (AUDIT-FINDINGS.md addendum 2026-08-24). Founder decisions 2026-08-24
    // (quest D10): show the dated snapshot of Moyee's own blog stories
    // instead of only an error, labelled "as at" so it is never mistaken
    // for live data (CLAUDE.md §10). Late-batch design change: every
    // section shows the SAME latest-10 list across all blog categories -
    // per-section filtering is superseded (see snapshot.ts). The honest
    // error state remains the final fallback should the snapshot ever be
    // empty.
    const snapshotStories = SNAPSHOT_STORIES;

    if (snapshotStories.length === 0) {
      return (
        <NewsSectionContainer>
          <NewsStateMessage>
            We couldn&apos;t load the latest news right now. Our stories are still available on the
            blog.
          </NewsStateMessage>
          <ViewAllNewsButton href="https://www.moyeecoffee.com/blog/" target="_blank">
            VIEW ALL NEWS
          </ViewAllNewsButton>
        </NewsSectionContainer>
      );
    }

    return (
      <NewsSectionContainer>
        {/* "Stories from our blog, as at ..." superseded 2026-09-02 (founder):
            replaced by the newsletter capture. The visible "as at" snapshot
            date was removed 2026-09-03 (founder decision) - snapshot
            staleness is no longer visible to visitors; recapture cadence or
            auto-dating is an open follow-up (PROGRESS.md). */}
        <NewsCaptureBlock>
          <JoinInCapture
            sentence="We keep you in the loop about coffee farming and how we are making a difference. Want these stories straight to your inbox?"
            source="latest-news"
            project={project}
            align="center"
          />
        </NewsCaptureBlock>
        {/* Same styled component the old "Stories from our blog" line used, so
            size/weight/color/centering match it exactly (founder, 2026-09-02). */}
        <NewsStateMessage>Below is a preview of what has been going on...</NewsStateMessage>
        <NewsGrid>
          <SliderWrapper>
            {/* Arrows on for the snapshot slider (founder, 2026-08-24);
                swipe already works via swipeToSlide. Arrow visuals are
                styled in News.styles.ts - no slick theme CSS is loaded. */}
            <Slider {...sliderSettings} arrows>
              {snapshotStories.map((story) => (
                <NewsCard
                  key={story.url}
                  onClick={() => {
                    onStoryClick?.();
                    window.open(story.url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {/* Cover images hotlink from moyeecoffee.com (verified at
                      capture); a story without one gets the branded yellow
                      fallback, not the grey box (founder, 2026-08-24). */}
                  {story.image ? <NewsImage src={story.image} alt="" /> : <SnapshotImageFallback />}
                  <NewsContent>
                    <NewsTitle>{truncateText(story.title, TITLE_LENGTH)}</NewsTitle>
                    <NewsDateTime>
                      <img src={calendarSvg} />
                      {new Date(story.date).toLocaleDateString()}
                    </NewsDateTime>
                    <NewsDescription>{truncateText(story.summary, MAX_LENGTH)}</NewsDescription>
                    <ReadMore>Read more...</ReadMore>
                  </NewsContent>
                </NewsCard>
              ))}
            </Slider>
          </SliderWrapper>
        </NewsGrid>
        <ViewAllNewsButton href="https://www.moyeecoffee.com/blog/" target="_blank">
          VIEW ALL NEWS
        </ViewAllNewsButton>
      </NewsSectionContainer>
    );
  }

  if (filteredPosts.length === 0) {
    return (
      <NewsSectionContainer>
        <NewsStateMessage>
          No news stories to show here yet. Visit the blog for the latest from Moyee.
        </NewsStateMessage>
        <ViewAllNewsButton href="https://www.moyeecoffee.com/blog/" target="_blank">
          VIEW ALL NEWS
        </ViewAllNewsButton>
      </NewsSectionContainer>
    );
  }

  return (
    <NewsSectionContainer>
      <NewsGrid>
        <SliderWrapper>
          <Slider {...sliderSettings}>
            {filteredPosts.map((post, index) => (
              <NewsCard
                key={index}
                onClick={() => {
                  onStoryClick?.();
                  setSelectedPost(post);
                }}
              >
                {post._embedded['wp:featuredmedia'][0].source_url ? (
                  <NewsImage src={post._embedded['wp:featuredmedia'][0].source_url} />
                ) : (
                  <ImageNotAvailable />
                )}
                <NewsContent>
                  <NewsTitle>{truncateText(post.title.rendered, TITLE_LENGTH)}</NewsTitle>
                  <CategoryWrapper>
                    {post.categories.map((catId) => (
                      <Category key={catId}>{categories[catId]} </Category>
                    ))}
                  </CategoryWrapper>
                  <NewsDateTime>
                    <img src={calendarSvg} />
                    {new Date(post.date).toLocaleDateString()}
                  </NewsDateTime>
                  <NewsDescription>
                    {parse(truncateText(post.excerpt.rendered, MAX_LENGTH))}
                  </NewsDescription>
                  <ReadMore>Read more...</ReadMore>
                </NewsContent>
              </NewsCard>
            ))}
          </Slider>
        </SliderWrapper>
      </NewsGrid>
      <ViewAllNewsButton href="https://www.moyeecoffee.com/blog/" target="_blank">
        VIEW ALL NEWS
      </ViewAllNewsButton>

      {selectedPost && (
        <ModalOverlay>
          <ModalContent>
            <CloseButton onClick={handleCloseModal}>
              <img src={closeSvg} />
            </CloseButton>
            <ModalImage src={selectedPost._embedded['wp:featuredmedia'][0].source_url} />
            <ModalWrapper>
              <ModalTitle>{selectedPost.title.rendered}</ModalTitle>
              <ModalDateTime>
                <img src={calendarSvg} /> {new Date(selectedPost.date).toLocaleDateString()}
              </ModalDateTime>
              <ModalDescription>{parse(selectedPost.excerpt.rendered)}</ModalDescription>
              <Box>
                <ViewPost href={selectedPost.guid.rendered} target="_blank">
                  VIEW POST <img src={arrowBack} />
                </ViewPost>
              </Box>
            </ModalWrapper>
          </ModalContent>
        </ModalOverlay>
      )}
    </NewsSectionContainer>
  );
};
